import React from "react";
import { Users, Lock, Shield, Star } from "lucide-react";
import PingIndicator from "./PingIndicator";
import MapThumbnail from "./MapThumbnail";
import RegionFlag from "./RegionFlag";
import { useFavorites } from "../context/FavoritesContext";
import { lastSeenShort, formatDuration } from "../lib/format";

const officialBadgeStyle = (isOfficial) =>
  isOfficial
    ? {
        background: "color-mix(in srgb, var(--official-color) 15%, transparent)",
        color: "var(--official-color)",
        borderColor: "color-mix(in srgb, var(--official-color) 55%, transparent)",
      }
    : {
        background: "color-mix(in srgb, var(--primary-accent) 10%, transparent)",
        color: "var(--primary-accent)",
        borderColor: "color-mix(in srgb, var(--primary-accent) 35%, transparent)",
      };

const StarBtn = ({ server, size = "sm" }) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const fav = isFavorite(server.id);
  const w = size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        toggleFavorite(server);
      }}
      data-testid={`fav-btn-${server.id}`}
      className="p-1.5 border transition-transform hover:scale-110"
      style={{
        borderColor: fav ? "var(--highlight)" : "var(--border-color)",
        background: fav ? "color-mix(in srgb, var(--highlight) 15%, transparent)" : "transparent",
        borderRadius: 2,
      }}
      aria-label={fav ? "Remove favorite" : "Add favorite"}
      aria-pressed={fav}
    >
      <Star
        className={w}
        style={{
          color: fav ? "var(--highlight)" : "var(--text-dim)",
          fill: fav ? "var(--highlight)" : "transparent",
        }}
      />
    </button>
  );
};

export const DesktopRow = ({ server, onOpen, isLastFavorite = false }) => {
  const fillPct = server.max_players > 0 ? Math.round((server.players / server.max_players) * 100) : 0;
  const offline = !server.online;
  // Ping anomaly: current ping >= 2× rolling 24h avg (and avg at least 20ms
  // to suppress false positives on near-local servers).
  const pingAvg = server.ping_avg_24h;
  const pingAnomaly =
    !offline && pingAvg && pingAvg >= 20 && server.ping >= pingAvg * 2;
  return (
    <tr
      className="server-row cursor-pointer border-b"
      style={{
        borderColor: isLastFavorite ? "var(--highlight)" : "var(--border-color)",
        borderBottomWidth: isLastFavorite ? 2 : 1,
        boxShadow: isLastFavorite
          ? "inset 0 -3px 0 color-mix(in srgb, var(--highlight) 50%, transparent)"
          : (pingAnomaly ? "inset 3px 0 0 var(--ping-high)" : "none"),
        background: pingAnomaly
          ? "color-mix(in srgb, var(--ping-high) 8%, transparent)"
          : undefined,
        opacity: offline ? 0.55 : 1,
        filter: offline ? "saturate(0.4)" : "none",
      }}
      onClick={() => onOpen(server)}
      data-testid={`server-row-${server.id}`}
      {...(pingAnomaly ? { "data-ping-anomaly": "true" } : {})}
      {...(isLastFavorite ? { "data-last-favorite": "true" } : {})}
    >
      <td className="py-3 px-3 w-6">
        <span
          className={`inline-block w-2 h-2 rounded-full${offline ? " blink" : ""}`}
          style={{
            background: offline ? "var(--ping-high)" : "var(--highlight)",
            boxShadow: offline
              ? "0 0 6px color-mix(in srgb, var(--ping-high) 60%, transparent)"
              : "0 0 8px var(--highlight)",
          }}
          data-testid={`server-status-${server.id}`}
        />
      </td>
      <td className="py-3 px-3 w-10">
        <StarBtn server={server} />
      </td>
      <td className="py-3 px-3 min-w-[280px] max-w-[420px]">
        <div className="flex items-center gap-2">
          {server.password_protected && (
            <Lock className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--secondary-accent)" }} />
          )}
          <span
            className="font-head font-bold tracking-wide uppercase truncate"
            style={{ color: offline ? "var(--text-secondary)" : "var(--text-primary)" }}
          >
            {server.name}
          </span>
        </div>
        <div className="font-mono-hud text-xs mt-0.5 truncate" style={{ color: "var(--text-dim)" }}>
          {server.id} · {server.tickrate}Hz · {server.anti_cheat}
        </div>
      </td>
      <td className="py-3 px-3 whitespace-nowrap">
        <div className="flex items-center gap-2 font-mono-hud text-sm tabular-nums">
          <Users className="w-3.5 h-3.5" style={{ color: offline ? "var(--text-dim)" : "var(--primary-accent)" }} />
          {offline ? (
            <span style={{ color: "var(--text-dim)" }}>
              — <span>/{server.max_players}</span>
            </span>
          ) : (
            <span style={{ color: "var(--text-primary)" }}>
              {server.players}
              <span style={{ color: "var(--text-dim)" }}>/{server.max_players}</span>
              <span className="hud-label ml-2" style={{ color: "var(--text-dim)" }}>{fillPct}%</span>
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-3 max-w-[260px]">
        {server.source === "wardogs" ? (
          <div className="flex items-center gap-2">
            <MapThumbnail
              variant={server.map_variant || server.map}
              base={server.map_base}
              width={80}
              height={45}
              label={false}
              testId={`map-thumb-${server.id}`}
            />
            <div className="min-w-0">
              <div className="font-body text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                {server.map_variant || server.map}
              </div>
              {server.online && server.current_map_duration_min != null && (
                <div
                  className="font-mono-hud text-[10px] tracking-widest mt-0.5"
                  style={{ color: "var(--primary-accent)" }}
                  title="How long the current map has been in play"
                  data-testid={`row-playing-${server.id}`}
                >
                  Playing {formatDuration(server.current_map_duration_min)}
                </div>
              )}
              <div
                className="font-mono-hud text-[10px] tracking-widest mt-0.5 inline-flex items-center gap-1"
                style={{ color: "var(--text-dim)" }}
                data-testid={`row-region-${server.id}`}
              >
                <RegionFlag region={server.region} size={12} testId={`region-flag-${server.id}`} />
                <span>{server.region}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <div
              className="block truncate font-body"
              style={{
                color: "var(--text-primary)",
                fontSize: (server.map || "").length > 24 ? "0.70rem" : "0.875rem",
                lineHeight: 1.25,
                maxWidth: 200,
              }}
              title={server.map}
              data-testid={`map-text-${server.id}`}
            >
              {server.map}
            </div>
            <div
              className="font-mono-hud text-[10px] tracking-widest mt-1 inline-flex items-center gap-1"
              style={{ color: "var(--text-dim)" }}
              data-testid={`row-region-${server.id}`}
            >
              <RegionFlag region={server.region} size={12} testId={`region-flag-${server.id}`} />
              <span>{server.region}</span>
            </div>
          </div>
        )}
      </td>
      <td className="py-3 px-3 whitespace-nowrap">
        <span className="font-body text-sm" style={{ color: "var(--text-secondary)" }}>{server.mode}</span>
      </td>
      <td className="py-3 px-3 whitespace-nowrap">
        <span className="px-2 py-1 border font-mono-hud text-[10px] tracking-widest" style={officialBadgeStyle(server.official)}>
          {server.official ? (
            <><Shield className="w-3 h-3 inline mr-1 -mt-0.5" />OFFICIAL</>
          ) : (
            "COMMUNITY"
          )}
        </span>
      </td>
      <td className="py-3 px-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <PingIndicator ping={server.ping} online={server.online} server={server} testId={`server-ping-${server.id}`} />
          {pingAnomaly && (
            <span
              className="inline-flex items-center font-mono-hud text-[10px] font-bold px-1 py-0.5 border"
              style={{
                color: "var(--ping-high)",
                borderColor: "var(--ping-high)",
                background: "color-mix(in srgb, var(--ping-high) 15%, transparent)",
                borderRadius: 2,
              }}
              title={`Ping ${server.ping}ms is ${(server.ping / pingAvg).toFixed(1)}× the 24h average (${pingAvg}ms) — likely a spike.`}
              data-testid={`ping-anomaly-${server.id}`}
            >
              SPIKE
            </span>
          )}
        </div>
        {offline && server.last_online_at && (
          <div
            className="font-mono-hud text-[10px] mt-1 tracking-widest"
            style={{ color: "var(--text-dim)" }}
            data-testid={`server-last-seen-${server.id}`}
          >
            LAST SEEN {lastSeenShort(server.last_online_at)}
          </div>
        )}
      </td>
    </tr>
  );
};

export const MobileCard = ({ server, onOpen, isLastFavorite = false }) => {
  const fillPct = server.max_players > 0 ? Math.round((server.players / server.max_players) * 100) : 0;
  const offline = !server.online;
  const hasThumb = server.source === "wardogs";
  const pingAvg = server.ping_avg_24h;
  const pingAnomaly = !offline && pingAvg && pingAvg >= 20 && server.ping >= pingAvg * 2;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(server)}
      onKeyDown={(e) => (e.key === "Enter" ? onOpen(server) : null)}
      className="tac-card p-4 cursor-pointer transition-colors"
      style={{
        background: pingAnomaly ? "color-mix(in srgb, var(--ping-high) 8%, var(--bg-surface))" : "var(--bg-surface)",
        borderBottom: isLastFavorite ? "3px solid var(--highlight)" : undefined,
        boxShadow: pingAnomaly ? "inset 4px 0 0 var(--ping-high)" : undefined,
        opacity: offline ? 0.6 : 1,
        filter: offline ? "saturate(0.4)" : "none",
      }}
      data-testid={`server-card-${server.id}`}
      {...(pingAnomaly ? { "data-ping-anomaly": "true" } : {})}
      {...(isLastFavorite ? { "data-last-favorite": "true" } : {})}
    >
      {hasThumb && (
        <div className="mb-3">
          <MapThumbnail
            variant={server.map_variant || server.map}
            base={server.map_base}
            width={"100%"}
            height={88}
          />
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-block w-2 h-2 rounded-full shrink-0${offline ? " blink" : ""}`}
              style={{
                background: offline ? "var(--ping-high)" : "var(--highlight)",
                boxShadow: offline
                  ? "0 0 6px color-mix(in srgb, var(--ping-high) 60%, transparent)"
                  : "0 0 8px var(--highlight)",
              }}
            />
            {server.password_protected && (
              <Lock className="w-3 h-3" style={{ color: "var(--secondary-accent)" }} />
            )}
            <span
              className="font-head font-bold uppercase tracking-wide truncate text-sm"
              style={{ color: offline ? "var(--text-secondary)" : "var(--text-primary)" }}
            >
              {server.name}
            </span>
          </div>
          <div className="font-mono-hud text-[11px] flex items-center gap-1.5 flex-wrap" style={{ color: "var(--text-dim)" }}>
            <span className="truncate">{server.id}</span>
            <span>·</span>
            <RegionFlag region={server.region} size={12} testId={`mobile-region-flag-${server.id}`} />
            <span>{server.region}</span>
            <span>·</span>
            <span>{server.tickrate}Hz</span>
          </div>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          <StarBtn server={server} />
          <div className="flex flex-col items-end gap-1">
            <PingIndicator ping={server.ping} online={server.online} server={server} />
            {offline && server.last_online_at && (
              <span
                className="font-mono-hud text-[10px] tracking-widest"
                style={{ color: "var(--text-dim)" }}
                data-testid={`server-last-seen-mobile-${server.id}`}
              >
                LAST {lastSeenShort(server.last_online_at)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        <div>
          <div className="hud-label">MAP</div>
          <div
            style={{
              color: "var(--text-primary)",
              fontSize: (server.map || "").length > 24 ? "0.70rem" : undefined,
            }}
            className="truncate"
            title={server.map}
          >
            {server.map}
          </div>
          {server.online && server.current_map_duration_min != null && (
            <div
              className="font-mono-hud text-[10px] mt-0.5"
              style={{ color: "var(--primary-accent)" }}
              data-testid={`mobile-playing-${server.id}`}
            >
              Playing {formatDuration(server.current_map_duration_min)}
            </div>
          )}
        </div>
        <div>
          <div className="hud-label">MODE</div>
          <div style={{ color: "var(--text-primary)" }} className="truncate">{server.mode}</div>
        </div>
        <div>
          <div className="hud-label">PLAYERS</div>
          <div className="font-mono-hud" style={{ color: offline ? "var(--text-dim)" : "var(--text-primary)" }}>
            {offline ? "— " : server.players}
            {!offline && "/"}
            {offline ? `/ ${server.max_players}` : server.max_players}
            {!offline && <span className="hud-label ml-2">{fillPct}%</span>}
          </div>
        </div>
        <div>
          <div className="hud-label">TYPE</div>
          <span className="px-1.5 py-0.5 border font-mono-hud text-[9px] tracking-widest inline-block" style={officialBadgeStyle(server.official)}>
            {server.official ? "OFFICIAL" : "COMMUNITY"}
          </span>
        </div>
      </div>
      {server.modifiers && server.modifiers.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid={`server-modifiers-${server.id}`}>
          {server.modifiers.map((m) => (
            <span
              key={m}
              className="font-mono-hud text-[9px] tracking-widest px-1.5 py-0.5 border"
              style={{
                color: "var(--primary-accent)",
                borderColor: "color-mix(in srgb, var(--primary-accent) 40%, transparent)",
                background: "color-mix(in srgb, var(--primary-accent) 10%, transparent)",
                borderRadius: 2,
              }}
            >
              {m}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
